<template>
  <form @submit.prevent="tryToSubmit">
    <b-field>
      <b-input
        v-model="term"
        :expanded="true"
        :placeholder="$t('Search')"
        type="search"
        icon="search"
        :aria-label="$t('Search')"
      >
      </b-input>
      <p class="control">
        <b-button
          v-if="hasTerm"
          @click="reset"
          icon-left="times"
          tag="a"
          type="button"
        >
        </b-button>
      </p>
      <p class="control">
        <b-button
          @click="tryToSubmit"
          :disabled="!canSubmit"
          tag="button"
          :loading="isLoading"
          type="submit"
          class="is-primary"
          :aria-label="$t('Search')"
        >
          {{ $t("Search") }}
        </b-button>
      </p>
    </b-field>
    <Level>
      <template #right>
        <div class="has-text-align-right" style="margin-right: 4.75rem">
          <a
            :aria-label="$t('Find terms')"
            @click="isFindTermsVisible = true"
            class="has-text-primary"
          >
            {{ $t("Find terms") }}
          </a>
          <FindTerms @input="paste" :visible.sync="isFindTermsVisible" />
          <a
            size="is-medium"
            type="is-primary"
            @click="showHelp('short-search-tips')"
            class="has-text-primary"
            style="margin-left: 0.75rem"
            :aria-label="$t('Quick Guide')"
          >
            {{ $t("Quick Guide") }}
          </a>
        </div>
      </template>
    </Level>

    <SearchOptions>
      <SearchOptionFuzzy v-model="filters.fuzziness" />
    </SearchOptions>
  </form>
</template>

<script>
import SearchOptions from "@/components/SearchOptions.vue";
import SearchOptionFuzzy from "@/components/SearchOptionFuzzy.vue";
import utilsMixin from "@/mixins/utils.js";
import FindTerms from "@/components/SearchFindTerms.vue";
import HelpShortSearchTips from "@/components/HelpShortSearchTips.vue";
import Level from "@/components/Level.vue";

import { debounce } from "@/utils/misc.js";

import { createValidator } from "@/utils/validation.js";
import { createMapper } from "@/utils/mapping.js";

const validator = createValidator("search-form");
const mapper = createMapper("search-form");

export default {
  components: {
    SearchOptions,
    SearchOptionFuzzy,
    FindTerms,
    Level,
  },
  mixins: [utilsMixin],
  data() {
    return {
      isLoading: false,
      isFiltersOpen: false,
      isFindTermsVisible: false,
      term: "",
      filters: {
        types: [],
        titles: [],
        fuzziness: 1,
      },
    };
  },
  watch: {
    "$route.query": {
      handler: "syncFromQuery",
      immediate: true,
      deep: true,
    },
  },
  computed: {
    canSubmit() {
      return !!this.term;
    },
    hasChanges() {
      return !this.isDeepEqual(
        this.cleanQuery(this.query),
        this.cleanQuery(mapper.fromURL(this.$route.query))
      );
    },
    routeQuery() {
      return mapper.fromURL(this.$route.query);
    },
    hasTerm() {
      return !!this.routeQuery.term;
    },
    query() {
      return {
        term: this.term,
        ...this.filters,
      };
    },
  },
  mounted() {
    this.tryToSubmit();
  },
  methods: {
    syncFromQuery(query) {
      let {
        term,
        types = [],
        titles = [],
        range = [],
        h_0 = [],
        h_1 = [],
        h_2 = [],
        fuzziness = null,
      } = this.cleanQuery(mapper.fromURL(query));
      if (fuzziness === null) {
        fuzziness = validator.getDefault("fuzziness");
      }

      this.term = term;
      Object.assign(this.filters, {
        types,
        titles,
        range,
        fuzziness,
        h_0,
        h_1,
        h_2,
      });
    },
    syncWithQuery() {
      const query = mapper.toURL(this.cleanQuery(this.query));
      this.$router.push({ query });
    },
    cleanQuery(query) {
      // FIXME: handle valid parameters
      if (validator.isValid(query)) {
        return validator.clean(query);
      } else {
        // clear everything
        return {};
      }
    },
    reset() {
      this.term = "";
      Object.assign(this.filters, {
        types: [],
        gemeinden: [],
      });
      this.syncWithQuery();
    },
    tryToSubmit() {
      if (this.canSubmit) {
        this.submit();
      }
    },
    paste(terms) {
      this.term = terms.join(" OR ");
    },
    showHelp(type) {
      if (type === "short-search-tips") {
        this.$buefy.modal.open({
          parent: this,
          component: HelpShortSearchTips,
          width: "480px",
          scroll: "keep",
        });
      }
    },
    submit: debounce(function () {
      this.syncWithQuery();
      this.isFiltersOpen = false;
      this.$emit("submit", this.query);
    }, 50),
  },
};
</script>
